import { getStats, baseVitality } from "../../../engine/itemEngine";

// ─── Moteur de combat Duel — Beta v1 ──────────────────────────────────────────
// Le résultat dépend PRINCIPALEMENT des stats d'équipement (~50% de l'impact),
// le reste est du hasard contrôlé. Le Level n'a AUCUN impact direct (il aide
// seulement à dropper/équiper de meilleurs items → impact indirect assumé).
//
// TODO Beta v1.1 : spécialiser chaque stat (Technique = occasions/dribbles/buts,
//   Defense = interceptions/saves, Physical = duels, Speed = transitions/pressing).
// TODO Beta v1.1 : brancher les spells (cosmétiques pour l'instant).
// TODO Beta v1.1 : brancher les formations (3-1 défensif / 2-2 équilibré / 1-3 offensif).

export interface EquipmentStats {
  technique: number;
  defense: number;
  physique: number;
  vitesse: number;
}

const EMPTY_STATS: EquipmentStats = { technique: 10, defense: 10, physique: 10, vitesse: 10 };

interface ItemLike { iid: string; slot: string; rarity: string; name?: string; stats?: Record<string, number> }

/** Somme les stats des items équipés (technique/defense/physique/vitesse).
 *  Quand `level` est fourni, la vitesse de base devient le socle de vitalité (HP)
 *  garanti par le niveau (baseVitality) — utilisé par le PVE. Sans `level`, on
 *  garde l'ancienne base fixe (10) pour ne pas déséquilibrer le Duel. */
export function getEquipmentStats(
  inventory: ItemLike[] | undefined,
  equipped: Record<string, string | null> | undefined,
  level?: number
): EquipmentStats {
  const baseVit = level == null ? EMPTY_STATS.vitesse : baseVitality(level);
  const out: EquipmentStats = { ...EMPTY_STATS, vitesse: baseVit };
  Object.values(equipped || {}).forEach((iid) => {
    if (!iid) return;
    const it = (inventory || []).find((x) => x.iid === iid);
    if (!it) return;
    const s = getStats(it);
    out.technique += s.technique || 0;
    out.defense += s.defense || 0;
    out.physique += s.physique || 0;
    out.vitesse += s.vitesse || 0;
  });
  return out;
}

/** Somme le bonus d'énergie PVE (affixe des ballons epic/legendary) des items équipés. */
export function getEquipmentEnergyBonus(
  inventory: ItemLike[] | undefined,
  equipped: Record<string, string | null> | undefined
): number {
  let bonus = 0;
  Object.values(equipped || {}).forEach((iid) => {
    if (!iid) return;
    const it = (inventory || []).find((x) => x.iid === iid);
    if (!it) return;
    bonus += (getStats(it) as Record<string, number>).energy || 0;
  });
  return bonus;
}

/** Puissance globale d'une équipe = somme des 4 stats. */
export function calculateTeamPower(stats: EquipmentStats): number {
  return stats.technique + stats.defense + stats.physique + stats.vitesse;
}

// Amplification de l'écart de stuff. Calibré pour :
//   égal → 50% · +20% stuff → ~58% · +50% → ~68% · x2 → ~80% · x3+ → cap 90%.
const ADVANTAGE_AMP = 1.8;
const MIN_ADVANTAGE = 0.1;
const MAX_ADVANTAGE = 0.9;

/**
 * Transforme l'écart de puissance en probabilité de victoire du joueur (0.10–0.90).
 * Jamais 0% ni 100% : il reste toujours une petite chance d'upset.
 */
export function calculateMatchAdvantage(playerPower: number, opponentPower: number): number {
  const total = playerPower + opponentPower;
  if (total <= 0) return 0.5;
  const ratio = playerPower / total; // 0.5 à stuff égal
  const advantage = 0.5 + (ratio - 0.5) * ADVANTAGE_AMP;
  return Math.max(MIN_ADVANTAGE, Math.min(MAX_ADVANTAGE, advantage));
}

/**
 * Stats simulées d'un bot, cohérentes avec son MMR (indicatif, pas déterministe
 * du résultat). Le MMR ne sert qu'à générer un adversaire crédible.
 */
export function simulateBotStats(mmr: number): EquipmentStats {
  // Échelle ELO départ 100 : ~0 MMR → power faible (~50), ~700 MMR → élevé (~120).
  const base = Math.max(20, Math.round((mmr + 500) / 10));
  const jitter = () => Math.round(base * (0.85 + Math.random() * 0.3) / 4);
  return {
    technique: jitter(),
    defense: jitter(),
    physique: jitter(),
    vitesse: jitter(),
  };
}
